/**
 * SavedChartLayouts — named chart presets (interval + overlays + layout).
 *
 * Phase D deliverable. Snapshots the chart's current view state to
 * localStorage under `chart_layouts.v1` and lets the user recall any
 * preset by name. Useful for switching between "Cloud Setup" and
 * "Pure Price Action" without re-clicking 15 chips each time.
 */
import React, { useEffect, useState } from 'react';
import {
  Menu, MenuItem, IconButton, Button, Tooltip, Box, Typography, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip, Stack,
} from '@mui/material';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useConfirm } from '../common/ConfirmDialog';

const STORAGE_KEY = 'chart_layouts.v1';
const MAX_LAYOUTS = 24;

export interface SavedLayoutState {
  layoutMode?: string;
  intervals?: number[];
  overlays?: Record<string, boolean>;
  /** trigger levels copied verbatim — restored when the user reloads the preset
   *  on the same symbol; on a different symbol they're loaded but the user can
   *  clear them if irrelevant. */
  triggerLevels?: Record<string, number | undefined>;
}

export interface SavedLayout {
  id: string;       // uuid-ish (timestamp + random suffix)
  name: string;
  state: SavedLayoutState;
  createdAt: number;
  updatedAt: number;
}

const load = (): SavedLayout[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw.filter(x => x && typeof x.name === 'string' && x.state);
  } catch {
    return [];
  }
};

const persist = (rows: SavedLayout[]) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rows.slice(0, MAX_LAYOUTS))); } catch { /* ignore */ }
};

interface Props {
  /** Snapshot the current chart state — used by "Save current" button. */
  captureState: () => SavedLayoutState;
  /** Apply a saved state back to the chart. */
  applyState: (state: SavedLayoutState) => void;
  /** Reset the chart to default (single pane, default interval, default overlays). */
  resetLayout: () => void;
}

const SavedChartLayouts: React.FC<Props> = ({ captureState, applyState, resetLayout }) => {
  const confirm = useConfirm();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [layouts, setLayouts] = useState<SavedLayout[]>(load());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [overwriteId, setOverwriteId] = useState<string | null>(null);

  // Re-sync from localStorage when menu opens (covers multi-tab edits)
  useEffect(() => {
    if (anchorEl) setLayouts(load());
  }, [anchorEl]);

  const closeMenu = () => setAnchorEl(null);

  const openSaveDialog = () => {
    setDraftName('');
    setOverwriteId(null);
    setSaveDialogOpen(true);
    closeMenu();
  };

  const handleSave = () => {
    const name = draftName.trim();
    if (!name) return;
    const state = captureState();
    const now = Date.now();
    setLayouts(prev => {
      const existingIdx = overwriteId
        ? prev.findIndex(l => l.id === overwriteId)
        : prev.findIndex(l => l.name.toLowerCase() === name.toLowerCase());
      let next: SavedLayout[];
      if (existingIdx >= 0) {
        next = [...prev];
        next[existingIdx] = { ...next[existingIdx], name, state, updatedAt: now };
      } else {
        const fresh: SavedLayout = {
          id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
          name, state, createdAt: now, updatedAt: now,
        };
        next = [fresh, ...prev];
      }
      persist(next);
      return next;
    });
    setSaveDialogOpen(false);
  };

  const handleApply = (layout: SavedLayout) => {
    applyState(layout.state);
    closeMenu();
  };

  const handleDelete = async (layout: SavedLayout) => {
    const ok = await confirm({
      title: `Delete "${layout.name}"?`,
      body: 'The saved layout will be removed from this browser.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setLayouts(prev => {
      const next = prev.filter(l => l.id !== layout.id);
      persist(next);
      return next;
    });
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: 'Reset chart layout?',
      body: 'Returns to a single pane with the default interval and overlay set.',
      confirmLabel: 'Reset',
    });
    if (!ok) return;
    resetLayout();
    closeMenu();
  };

  const handleOverwrite = (layout: SavedLayout) => {
    setDraftName(layout.name);
    setOverwriteId(layout.id);
    setSaveDialogOpen(true);
    closeMenu();
  };

  return (
    <>
      <Tooltip title="Saved chart layouts">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.4 }}>
          <BookmarksIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        PaperProps={{ sx: { minWidth: 260, maxHeight: 420 } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Saved layouts</Typography>
          <Chip size="small" label={`${layouts.length}/${MAX_LAYOUTS}`} />
        </Box>
        <MenuItem onClick={openSaveDialog}>
          <SaveIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2">Save current view…</Typography>
        </MenuItem>
        <MenuItem onClick={handleReset}>
          <RefreshIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2">Reset to default</Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {layouts.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              No saved layouts yet. Configure the chart how you like it, then "Save current view".
            </Typography>
          </Box>
        ) : (
          layouts.map(l => (
            <MenuItem
              key={l.id}
              onClick={() => handleApply(l)}
              sx={{ display: 'flex', gap: 1, alignItems: 'center', pr: 1 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{l.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                  {new Date(l.updatedAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {' · '}
                  {Object.entries(l.state.overlays || {}).filter(([, v]) => v).length} overlays
                </Typography>
              </Box>
              <Tooltip title="Overwrite with current view">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOverwrite(l); }} sx={{ p: 0.25 }}>
                  <SaveIcon fontSize="small" sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(l); }} sx={{ p: 0.25 }}>
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))
        )}
      </Menu>

      {/* Save / overwrite dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{overwriteId ? 'Overwrite layout' : 'Save chart layout'}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Captures: layout mode, intervals per pane, overlay toggles, and current trigger levels.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Layout name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value.slice(0, 60))}
            placeholder="e.g. Cloud Setup · Daily"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
          />
          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
            {['Cloud Setup', 'Pure Price', 'Volume + OF', 'Pivots Only'].map(s => (
              <Chip key={s} label={s} size="small" onClick={() => setDraftName(s)} />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!draftName.trim()}>
            {overwriteId ? 'Overwrite' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedChartLayouts;
